import { useState, useCallback, useRef } from 'react';

/**
 * Parallel to usePlanManagement, but for Passages Plans (curated verse-level
 * plans like the Romans Road) instead of whole-chapter Reading Plans. Kept
 * fully separate rather than generalizing usePlanManagement, since the two
 * plan types have different Firestore shapes, different subscription
 * collections, and different completion semantics.
 *
 * Recording progress is per-user — each person records their own copy of a
 * plan. The "current" recording plan is tracked via users/{uid}.lastPassagePlanId
 * since a user can be subscribed to multiple plans simultaneously.
 */
export const usePassagePlanManagement = ({
  currentUser,
  userHasManuallyChanged,
  setUserHasManuallyChanged,
  setSelectedBook,
  setSelectedChapter,
  setStartVerse,
  setEndVerse,
  onPassageAdvancement,
}) => {
  const [currentPassagePlanName, setCurrentPassagePlanName] = useState(null);
  const [passagePlanId, setPassagePlanId] = useState(null);
  const [passagePlanPassages, setPassagePlanPassages] = useState([]);
  const [passagePlanCompletedIndexes, setPassagePlanCompletedIndexes] = useState(new Set());
  const [passagePlanFullyRecorded, setPassagePlanFullyRecorded] = useState(false);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(null);
  const isAdvancingRef = useRef(false);

  // Resolves the user's current recording plan subscription via lastPassagePlanId.
  const fetchCurrentPassagePlanSub = async (db, uid) => {
    const { doc, getDoc } = await import('firebase/firestore');
    const userDoc = await getDoc(doc(db, 'users', uid));
    const lastPlanId = userDoc.data()?.lastPassagePlanId;
    if (!lastPlanId) return null;
    const subDoc = await getDoc(doc(db, `users/${uid}/passagePlanSubscriptions`, lastPlanId));
    return subDoc.exists() ? subDoc : null;
  };

  // Current user's own recorded verse keys — progress is per-user, not family-wide.
  const fetchUserRecordedVerseKeys = async (db, userId) => {
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const keys = new Set();
    const snap = await getDocs(
      query(collection(db, 'recordings'), where('userId', '==', userId))
    );
    snap.docs.forEach(d => {
      const r = d.data();
      if (r.book && r.chapter && r.verse) keys.add(`${r.book}_${r.chapter}_${r.verse}`);
    });
    return keys;
  };

  const isPassageComplete = (passage, recordedKeySet) => {
    for (let v = passage.startVerse; v <= passage.endVerse; v++) {
      if (!recordedKeySet.has(`${passage.book}_${passage.chapter}_${v}`)) return false;
    }
    return true;
  };

  // Top-level entry point, mirrors advanceToNextVerses/advanceToNextPlanVerses.
  // Returns true if there's an active Passages Plan subscription (whether or
  // not it actually advanced anything) — the caller should skip falling back
  // to Reading Plan / default advancement in that case. Returns false if
  // there's no current Passages Plan.
  const advanceToNextPassage = useCallback(async () => {
    if (!currentUser) return false;
    if (isAdvancingRef.current) return true;
    isAdvancingRef.current = true;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');

      const activeSub = await fetchCurrentPassagePlanSub(db, currentUser.uid);
      if (!activeSub) {
        setCurrentPassagePlanName(null);
        setPassagePlanId(null);
        setPassagePlanPassages([]);
        return false;
      }

      const subData = activeSub.data();
      const planDoc = await getDoc(doc(db, 'passagePlans', subData.planId));
      if (!planDoc.exists()) {
        setCurrentPassagePlanName(null);
        return false;
      }

      const plan = planDoc.data();
      const passages = plan.passages || [];
      setCurrentPassagePlanName(plan.name);
      setPassagePlanId(subData.planId);
      setPassagePlanPassages(passages);

      if (userHasManuallyChanged) return true;

      const recordedKeySet = await fetchUserRecordedVerseKeys(db, currentUser.uid);

      const doneIndexes = new Set();
      let target = null;
      let targetIndex = -1;
      passages.forEach((p, i) => {
        if (isPassageComplete(p, recordedKeySet)) {
          doneIndexes.add(i);
        } else if (!target) {
          target = p;
          targetIndex = i;
        }
      });
      setPassagePlanCompletedIndexes(doneIndexes);
      setPassagePlanFullyRecorded(passages.length > 0 && doneIndexes.size === passages.length);

      if (!target) {
        setCurrentPassageIndex(null);
        return true;
      }

      setCurrentPassageIndex(targetIndex);
      setSelectedBook(target.book);
      setSelectedChapter(target.chapter);
      setStartVerse(target.startVerse);
      setEndVerse(target.endVerse);
      if (onPassageAdvancement) {
        onPassageAdvancement(target.book, target.chapter, target.startVerse, target.endVerse, targetIndex);
      }
      return true;
    } catch (error) {
      console.error('❌ Error advancing to next passage:', error);
      return false;
    } finally {
      isAdvancingRef.current = false;
    }
  }, [currentUser, userHasManuallyChanged, setSelectedBook, setSelectedChapter, setStartVerse, setEndVerse, onPassageAdvancement]);

  // Called after every successful upload. Writes per-user progress to
  // users/{uid}/passagePlanProgress/{planId}.
  const updatePassagePlanProgress = useCallback(async (userId, db) => {
    try {
      const sub = await fetchCurrentPassagePlanSub(db, userId);
      if (!sub) return;
      const subData = sub.data();

      const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const planDoc = await getDoc(doc(db, 'passagePlans', subData.planId));
      if (!planDoc.exists()) return;
      const plan = planDoc.data();
      const passages = plan.passages || [];

      const recordedKeySet = await fetchUserRecordedVerseKeys(db, userId);

      const doneIndexes = [];
      passages.forEach((p, i) => {
        if (isPassageComplete(p, recordedKeySet)) doneIndexes.push(i);
      });

      await setDoc(
        doc(db, `users/${userId}/passagePlanProgress`, subData.planId),
        {
          planId: subData.planId,
          planName: plan.name,
          totalPassages: passages.length,
          completedPassageIndexes: doneIndexes,
          lastRecordedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error updating passage plan progress:', error);
    }
  }, []);

  // Manual, plan-aware jump to a specific passage (e.g. "re-record verse 3
  // specifically") — pauses auto-advance like any manual change, but keeps
  // the plan name displayed since the user is still working within the plan.
  const jumpToPassage = useCallback((passage, index) => {
    setUserHasManuallyChanged(true);
    setCurrentPassageIndex(index);
    setSelectedBook(passage.book);
    setSelectedChapter(passage.chapter);
    setStartVerse(passage.startVerse);
    setEndVerse(passage.endVerse);
  }, [setUserHasManuallyChanged, setSelectedBook, setSelectedChapter, setStartVerse, setEndVerse]);

  // Resume auto-advancing through the plan (the Passages Plan equivalent of
  // usePlanManagement's handleFollowPlan).
  const resumeFollowingPassagePlan = useCallback(async () => {
    setUserHasManuallyChanged(false);
    await advanceToNextPassage();
  }, [setUserHasManuallyChanged, advanceToNextPassage]);

  return {
    currentPassagePlanName,
    passagePlanId,
    passagePlanPassages,
    passagePlanCompletedIndexes,
    passagePlanFullyRecorded,
    currentPassageIndex,

    advanceToNextPassage,
    updatePassagePlanProgress,
    jumpToPassage,
    resumeFollowingPassagePlan,
  };
};
